import { useState, useEffect, useCallback, KeyboardEvent } from 'react';
import { __ } from '@wordpress/i18n';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';
import { useBookingStore } from '../store/useBookingStore';
import { useBookingValidation } from '../hooks/useBookingValidation';
import {
	useService,
	useSelectedDate,
	useSelectedTime,
} from '../hooks/useBookingComputed';
import { useOriginalBookingDisplay } from '../hooks/useOriginalBookingDisplay';
import { formatCalendarDate } from '../utils/date-time';
import './ManualDateTimeInput.css';

interface DateInputProps {
	id: string;
	label: string;
	value: Date | undefined;
	onChange: (date: Date | undefined) => void;
	required?: boolean;
	min?: string;
}

/**
 * Parse a date string from an input[type="date"] safely.
 * Returns undefined if any component is invalid/incomplete.
 * Validates: year >= 1000, month 1-12, day 1-31.
 */
function parseDateInput(dateStr: string): Date | undefined {
	const parts = dateStr.split('-');
	if (parts.length !== 3) {
		return undefined;
	}

	const [yearStr, monthStr, dayStr] = parts;

	// Year must be exactly 4 digits and >= 1000 to be considered complete
	if (yearStr.length !== 4) {
		return undefined;
	}

	const year = parseInt(yearStr, 10);
	if (isNaN(year) || year < 1000) {
		return undefined;
	}

	const month = parseInt(monthStr, 10);
	if (isNaN(month) || month < 1 || month > 12) {
		return undefined;
	}

	const day = parseInt(dayStr, 10);
	if (isNaN(day) || day < 1 || day > 31) {
		return undefined;
	}

	// Create date and verify it's valid (handles edge cases like Feb 30)
	const date = new Date(year, month - 1, day);
	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return undefined;
	}

	return date;
}

function formatDateValue(date: Date | undefined): string {
	return date ? formatCalendarDate(date) : '';
}

/**
 * Controlled date input with local string state.
 * Updates parent only when the date is valid.
 * Syncs from parent when external value changes.
 */
function DateInput({
	id,
	label,
	value,
	onChange,
	required,
	min,
}: DateInputProps) {
	const externalValue = formatDateValue(value);
	const [localValue, setLocalValue] = useState(externalValue);

	// Sync from external value when it changes (e.g., calendar picker, reset)
	useEffect(() => {
		setLocalValue(externalValue);
	}, [externalValue]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const dateStr = e.target.value;
			setLocalValue(dateStr); // Always update local state for smooth typing

			if (!dateStr) {
				onChange(undefined);
				return;
			}

			// Only propagate to parent if valid
			const date = parseDateInput(dateStr);
			if (date) {
				onChange(date);
			}
		},
		[onChange]
	);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			e.currentTarget.blur();
		}
	};

	return (
		<div className="gpb-manual-input__field">
			<label htmlFor={id}>
				{label}
				{required && ' *'}
			</label>
			<input
				id={id}
				type="date"
				value={localValue}
				min={min}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				required={required}
			/>
		</div>
	);
}

interface TimeInputProps {
	id: string;
	label: string;
	value: string | undefined;
	onChange: (time: string | undefined) => void;
	required?: boolean;
}

/**
 * Time input - controlled, updates on every change.
 */
function TimeInput({ id, label, value, onChange, required }: TimeInputProps) {
	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			e.currentTarget.blur();
		}
	};

	return (
		<div className="gpb-manual-input__field">
			<label htmlFor={id}>
				{label}
				{required && ' *'}
			</label>
			<input
				id={id}
				type="time"
				value={value || ''}
				onChange={(e) => onChange(e.target.value || undefined)}
				onKeyDown={handleKeyDown}
				required={required}
			/>
		</div>
	);
}

export function ManualDateTimeInput() {
	const { data: service } = useService();
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);

	const selectedDate = useSelectedDate();
	const selectedTime = useSelectedTime();
	const selectedRange = useBookingStore((state) => state.selectedRange);
	const setSelectedDate = useBookingStore((state) => state.setSelectedDate);
	const setSelectedTime = useBookingStore((state) => state.setSelectedTime);
	const setSelectedRange = useBookingStore((state) => state.setSelectedRange);

	const originalBookingDisplay = useOriginalBookingDisplay();

	const { warnings, isValidating, validateBooking, clearWarnings } =
		useBookingValidation();

	// Determine if this is a full-day booking (1440 minutes = 24 hours)
	const isFullDay = service ? service.blockSize >= 1440 : false;
	const allowsFlexibleDuration = service?.flexibleDuration ?? false;

	// For date ranges, use selectedRange.from as the start date
	const startDate = selectedDate || selectedRange?.from;
	const endDate = selectedRange?.to;

	// Validate when date/time changes
	useEffect(() => {
		if (!startDate) {
			clearWarnings();
			return;
		}

		// Skip validation for non-full-day bookings until time is entered
		if (!isFullDay && !selectedTime) {
			clearWarnings();
			return;
		}

		// Build start datetime
		const dateStr = formatCalendarDate(startDate);
		const timeStr = selectedTime || '00:00';
		const startDatetime = `${dateStr} ${timeStr}`;

		// Build end datetime only if range is set
		let endDatetime: string | undefined;
		if (endDate) {
			const endDateStr = formatCalendarDate(endDate);
			const endTimeStr = isFullDay ? '23:59' : format(endDate, 'HH:mm');
			endDatetime = `${endDateStr} ${endTimeStr}`;
		}

		// Validate
		validateBooking({
			serviceId: currentServiceId,
			startDatetime,
			endDatetime,
			resourceIds: selectedResourceIds,
			quantity: 1,
			excludeBookingId: excludeBookingId || undefined,
		});
	}, [
		startDate,
		endDate,
		selectedTime,
		isFullDay,
		currentServiceId,
		selectedResourceIds,
		excludeBookingId,
		validateBooking,
		clearWarnings,
	]);

	// Handlers for start date changes
	const handleStartDateChange = (date: Date | undefined) => {
		if (!date) {
			setSelectedDate(undefined);
			setSelectedRange(undefined);
			return;
		}

		if (isFullDay && allowsFlexibleDuration) {
			setSelectedRange({
				from: date,
				to: endDate || date,
			});
		} else {
			setSelectedDate(date);
		}
	};

	// Handler for end date changes
	const handleEndDateChange = (date: Date | undefined) => {
		if (!date) {
			setSelectedRange(undefined);
			return;
		}

		let newEndDate = date;

		// Preserve time from existing endDate if it exists
		if (endDate) {
			newEndDate = setHours(newEndDate, endDate.getHours());
			newEndDate = setMinutes(newEndDate, endDate.getMinutes());
			newEndDate = setSeconds(newEndDate, 0);
		} else if (selectedTime) {
			const [hours, minutes] = selectedTime.split(':').map(Number);
			newEndDate = setHours(newEndDate, hours);
			newEndDate = setMinutes(newEndDate, minutes);
			newEndDate = setSeconds(newEndDate, 0);
		}

		if (startDate) {
			setSelectedRange({
				from: startDate,
				to: newEndDate,
			});
		}
	};

	// Group warnings by severity
	const errorWarnings = warnings.filter((w) => w.severity === 'error');
	const otherWarnings = warnings.filter((w) => w.severity !== 'error');

	return (
		<div className="gpb-manual-input">
			{originalBookingDisplay && (
				<div className="gpb-manual-input__original">
					<strong>{__('Original Booking:', 'gp-bookings')}</strong>{' '}
					{originalBookingDisplay}
				</div>
			)}

			<div className="gpb-manual-input__fields">
				<DateInput
					id="gpb-start-date"
					label={__('Start Date', 'gp-bookings')}
					value={startDate}
					onChange={handleStartDateChange}
					required
				/>

				{!isFullDay && (
					<TimeInput
						id="gpb-start-time"
						label={__('Start Time', 'gp-bookings')}
						value={selectedTime}
						onChange={setSelectedTime}
						required
					/>
				)}

				{allowsFlexibleDuration && (
					<>
						<DateInput
							id="gpb-end-date"
							label={__('End Date', 'gp-bookings')}
							value={endDate}
							onChange={handleEndDateChange}
							min={
								startDate
									? formatDateValue(startDate)
									: undefined
							}
						/>

						{!isFullDay && endDate && (
							<TimeInput
								id="gpb-end-time"
								label={__('End Time', 'gp-bookings')}
								value={format(endDate, 'HH:mm')}
								onChange={(time) => {
									if (!time || !startDate) {
										return;
									}
									const [hours, mins] = time
										.split(':')
										.map(Number);
									let newEndDate = new Date(endDate);
									newEndDate = setHours(newEndDate, hours);
									newEndDate = setMinutes(newEndDate, mins);
									newEndDate = setSeconds(newEndDate, 0);
									setSelectedRange({
										from: startDate,
										to: newEndDate,
									});
								}}
							/>
						)}
					</>
				)}
			</div>

			{isValidating && (
				<div className="gpb-validating">
					{__('Validating…', 'gp-bookings')}
				</div>
			)}

			{warnings.length > 0 && !isValidating && (
				<div className="gpb-validation-warnings">
					{errorWarnings.length > 0 && (
						<div className="gpb-warnings-group gpb-warnings-group--error">
							<h4>{__('Critical Issues', 'gp-bookings')}</h4>
							{errorWarnings.map((warning, i) => (
								<div key={i} className="gpb-warning">
									{warning.message}
								</div>
							))}
						</div>
					)}

					{otherWarnings.length > 0 && (
						<div className="gpb-warnings-group gpb-warnings-group--warning">
							<h4>{__('Warnings', 'gp-bookings')}</h4>
							{otherWarnings.map((warning, i) => (
								<div key={i} className="gpb-warning">
									{warning.message}
								</div>
							))}
						</div>
					)}

					<div className="gpb-admin-override-note">
						<strong>{__('Admin Override:', 'gp-bookings')}</strong>{' '}
						{__(
							'Saving this booking will ignore availability and capacity rules. This may result in the service or resource being overbooked.',
							'gp-bookings'
						)}
					</div>
				</div>
			)}
		</div>
	);
}
